import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Box,
} from "@mui/material";
import {
  SupervisorAccount as SupervisorAccountIcon,
  Work as WorkIcon,
  Build as BuildIcon,
  LocalOffer as LocalOfferIcon,
  Event as EventIcon,
  SupportAgent as SupportAgentIcon,
} from "@mui/icons-material";
import CampaignIcon from "@mui/icons-material/Campaign";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import API_BASE_URL from "../config";

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    jobs: 0,
    services: 0,
    offers: 0,
    events: 0,
    revenue: 0,
    supportTickets: 0,
    pendingApprovals: 0,
    bookings: 0,
    ads: 0,
    classifieds: 0,
  });

  const [revenueData, setRevenueData] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          usersRes,
          jobsRes,
          servicesRes,
          offersRes,
          eventsRes,
          supportTicketsRes,
          adsRes,
          classifiedsRes,
        ] = await Promise.all([
          axios.get(`${API_BASE_URL}/users`),
          axios.get(`${API_BASE_URL}/jobs/alljobs`),
          axios.get(`${API_BASE_URL}/services/allservices`),
          axios.get(`${API_BASE_URL}/offers/alloffers`),
          axios.get(`${API_BASE_URL}/events/allevents`),
          axios.get(`${API_BASE_URL}/support`),
          axios.get(`${API_BASE_URL}/sponsored-ads/allads`),
          axios.get(`${API_BASE_URL}/classifieds/allclassifieds`),
        ]);

        setStats({
          users: usersRes.data.total || usersRes.data.length || 0,
          jobs: jobsRes.data.total_jobs || jobsRes.data.length || 0,
          services: servicesRes.data.total_services || servicesRes.data.length || 0,
          offers: offersRes.data.total || offersRes.data.length || 0,
          events: eventsRes.data.total || eventsRes.data.length || 0,
          revenue: 150000, // Replace with dynamic revenue later
          supportTickets: supportTicketsRes.data.total || supportTicketsRes.data.length || 0,
          pendingApprovals: 20, // Optional: add dynamic logic
          bookings: 120, // Optional: add dynamic logic
          ads: adsRes.data.total || adsRes.data.length || 0,
          classifieds: classifiedsRes.data.total || classifiedsRes.data.length || 0,
        });

        // Dummy revenue chart data
        setRevenueData([
          { month: "Jan", revenue: 30000 },
          { month: "Feb", revenue: 45000 },
          { month: "Mar", revenue: 50000 },
          { month: "Apr", revenue: 60000 },
          { month: "May", revenue: 70000 },
          { month: "Jun", revenue: 80000 },
        ]);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching stats:", error);
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 4 }}>
      <Typography
        variant="h4"
        fontWeight="bold"
        textAlign="center"
        mb={3}
        color="#42326e"
      >
        Admin Dashboard
      </Typography>

      {loading ? (
        <CircularProgress sx={{ display: "block", margin: "auto" }} />
      ) : (
        <>
          {/* Dashboard Cards */}
          <Grid container spacing={3}>
            {[
              {
                label: "Total Users",
                count: stats.users,
                icon: <SupervisorAccountIcon sx={{ color: "#42326e" }} />,
                link: "/admin/users",
              },
              {
                label: "Total Jobs",
                count: stats.jobs,
                icon: <WorkIcon sx={{ color: "#42326e" }} />,
                link: "/admin/jobs",
              },
              {
                label: "Total Services",
                count: stats.services,
                icon: <BuildIcon sx={{ color: "#42326e" }} />,
                link: "/admin/services",
              },
              {
                label: "Total Offers",
                count: stats.offers,
                icon: <LocalOfferIcon sx={{ color: "#42326e" }} />,
                link: "/admin/offers",
              },
              {
                label: "Total Classifieds",
                count: stats.classifieds,
                icon: <CampaignIcon sx={{ color: "#42326e" }} />,
                link: "/admin/classifieds",
              },
              {
                label: "Total Ads",
                count: stats.ads,
                icon: <LocalOfferIcon sx={{ color: "#42326e" }} />,
                link: "/admin/ads",
              },
              {
                label: "Total Events",
                count: stats.events,
                icon: <EventIcon sx={{ color: "#42326e" }} />,
                link: "/admin/events",
              },
              {
                label: "Support Tickets",
                count: stats.supportTickets,
                icon: <SupportAgentIcon sx={{ color: "#42326e" }} />,
                link: "/admin/support",
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ p: 2, textAlign: "center", boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ fontSize: 50 }}>{item.icon}</Box>
                    <Typography variant="h6" fontWeight="bold">
                      {item.label}
                    </Typography>
                    <Typography variant="h4">{item.count}</Typography>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#42326e",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "black",
                          color: "white",
                        },
                        mt: 2,
                      }}
                      component={Link}
                      to={item.link}
                    >
                      Manage
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Revenue Chart */}
          <Typography
            variant="h5"
            fontWeight="bold"
            textAlign="center"
            mt={5}
            mb={3}
          >
            Revenue Trend (Last 6 Months)
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#ff7300"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Container>
  );
};

export default AdminDashboard;
